import { ExposedAssetsTable } from "~/pages/space/vulnerabilities/components/exposed-assets/components/ExposedAssetsTable";
import { useExposedAssets } from "~/pages/space/vulnerabilities/components/exposed-assets/hooks/useExposedAssets";
import {
  ExposedAssetsAdvisoryNode,
  ExposedAssetsCVENode,
  AssetContextualLinkType,
  SelectedEntityWithExposedAssetData,
  SelectionProps,
} from "~/pages/space/vulnerabilities/components/exposed-assets/types";
import { SectionHeading } from "~/components/DetailsPage/components";
import { EmptySection } from "~/components/vulnerabilities";
import { Grid } from "@mui/material";
import { Loading, LoadingFailed } from "~/components/loading";
import { SpaceOrWorkspaceScope } from "~/hooks/useScope";
import { FindingsFilter } from "~/operations";
import { Flex } from "~/components/Flex";
import { NoMatchFilters } from "~/pages/inventory/components/EmptyState";
import { FleetIcon } from "~/icons";
import { SelectedEntityWithException } from "~/components/exceptions/types";
import { SelectedEntityWithFindingData } from "~/components/exceptions/SetExceptionDialog";

interface ExposedAssetsAdapterProps {
  scope: SpaceOrWorkspaceScope;
  selectionProps?: SelectionProps | undefined;
  emptyStateMessage?: string;
  urlContextType: (typeof AssetContextualLinkType)[keyof typeof AssetContextualLinkType];
  contextId: string;
  filter: FindingsFilter;
  onCreateTicketClick?: (params: {
    selectedAssets: Array<
      SelectedEntityWithExposedAssetData &
        SelectedEntityWithException &
        SelectedEntityWithFindingData
    >;
    isScopeLevelTicket: boolean;
  }) => void;
  onCreatePullRequestClick?: (params: {
    selectedAssets: Array<
      SelectedEntityWithExposedAssetData &
        SelectedEntityWithException &
        SelectedEntityWithFindingData
    >;
    findings: Array<ExposedAssetsAdvisoryNode | ExposedAssetsCVENode>;
  }) => void;
  onSetExceptionClick?: (params: {
    selectedAssets: Array<
      SelectedEntityWithExposedAssetData &
        SelectedEntityWithException &
        SelectedEntityWithFindingData
    >;
  }) => void;
  onRemoveExceptionClick?: (params: {
    selectedAssets: Array<
      SelectedEntityWithExposedAssetData &
        SelectedEntityWithException &
        SelectedEntityWithFindingData
    >;
  }) => void;
}

export const ExposedAssets = ({
  scope,
  selectionProps,
  emptyStateMessage = "There are currently no exposed assets.",
  urlContextType,
  contextId,
  filter,
  onCreateTicketClick,
  onCreatePullRequestClick,
  onSetExceptionClick,
  onRemoveExceptionClick,
}: ExposedAssetsAdapterProps) => {
  const {
    assets,
    sort,
    paginate,
    filteredTotalCount,
    pageInfo,
    error,
    loading,
    hasAffectedAssets,
    activeFilterCount,
    filterForm,
    resetFilters,
  } = useExposedAssets({
    selectionProps,
    onCreateTicketClick,
    onCreatePullRequestClick,
    onSetExceptionClick,
    onRemoveExceptionClick,
    scope,
    filter,
  });

  if (error) {
    return <LoadingFailed what="assets" />;
  }

  if (loading) {
    return <Loading what="assets" />;
  }

  return (
    <Grid id="affected-assets" size={12}>
      <SectionHeading heading="Exposed assets" />
      <Flex flexDirection="column">
        {(hasAffectedAssets ||
          (!hasAffectedAssets && activeFilterCount > 0)) && (
          <Grid size={12} sx={{ mb: 3 }}>
            {filterForm}
          </Grid>
        )}
        {(assets?.length || 0) > 0 && !error ? (
          <ExposedAssetsTable
            assets={assets}
            sort={sort}
            scopeParams={scope.params}
            filteredTotalCount={filteredTotalCount}
            paginationProps={paginate.pageProps(pageInfo)}
            selectionProps={selectionProps}
            urlContextType={urlContextType}
            contextId={contextId}
          />
        ) : (
          <>
            {activeFilterCount > 0 ? (
              <NoMatchFilters
                title="No exposed assets match your filter selection"
                icon={
                  <FleetIcon sx={{ fontSize: 38, color: "primary.main" }} />
                }
                content={`Out of the exposed assets, none match the filters you selected. Reset them to reveal all exposed assets.`}
                onClick={() => {
                  resetFilters();
                }}
              />
            ) : (
              <EmptySection
                id="exposed-assets-list-empty"
                text={emptyStateMessage}
              />
            )}
          </>
        )}
      </Flex>
    </Grid>
  );
};
